import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Plus, Search } from 'lucide-react';
import { getAllFacilities } from '../../services/api/facilityService';
import { useToast } from '../../hooks/use-toast';
import FacilityDialog from './FacilityFormDialog';
import InviteSiteAdminDialog from './InviteSiteAdminDialog';
import DeactivateFacilityDialog from './DeactivateFacilityDialog';
import type { Facility } from './utils/utils';
import FacilitiesTable from './tables/FacilitiesTable';
import StackedSection from './tables/TableSection';
import { createOnboardingColumns } from './tables/config/onboardingColumns';
import { createTrialColumns } from './tables/config/trialColumns';
import { createActiveColumns } from './tables/config/activeColumns';
import {
    applyStageFilter,
    filterFacilitiesByStage,
    sortDeactivatedLast,
    sortFacilities,
} from './tables/utils/facilityTableFilters';
import type { FacilityStageKey } from './tables/utils/facilityTableTypes';
import {
    STAGE_TABS,
    CHIP_ACTIVE,
    CHIP_INACTIVE,
    getStageConfig,
    getDefaultSort,
} from './utils/facilityStageConfig';
import GuestsSection from './GuestsSection';
import LoadingRing from '../ui-components/LoadingRing';

type TabValue = 'all' | FacilityStageKey | 'guests';

export default function FacilitiesDashboard() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<TabValue>('all');
    const [filterValue, setFilterValue] = useState('all');
    const [sortValue, setSortValue] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [inviteTarget, setInviteTarget] = useState<Facility | null>(null);
    const [deactivateTarget, setDeactivateTarget] = useState<Facility | null>(
        null
    );

    const fetchFacilities = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAllFacilities();
            setFacilities(Array.isArray(data) ? data : []);
        } catch {
            setFacilities([]);
            toast({
                title: 'Could not load facilities',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchFacilities();
    }, [fetchFacilities]);

    useEffect(() => {
        setFilterValue('all');
        setSortValue('');
    }, [activeTab]);

    const grouped = useMemo(
        () => ({
            all: facilities,
            onboarding: sortDeactivatedLast(filterFacilitiesByStage(facilities, 'onboarding')),
            trial: sortDeactivatedLast(filterFacilitiesByStage(facilities, 'trial')),
            active: sortDeactivatedLast(filterFacilitiesByStage(facilities, 'active')),
        }),
        [facilities]
    );

    const stageKey: FacilityStageKey =
        activeTab === 'all' || activeTab === 'guests' ? 'onboarding' : activeTab;
    const { filterOptions, sortOptions } = getStageConfig(stageKey);
    const defaultSort = getDefaultSort(stageKey);

    const filteredRows = useMemo(() => {
        if (activeTab === 'all' || activeTab === 'guests') return [];

        const q = search.trim().toLowerCase();
        let filtered = grouped[activeTab];

        if (q) {
            filtered = filtered.filter(
                (f) =>
                    f.name.toLowerCase().includes(q) ||
                    f.region?.toLowerCase().includes(q)
            );
        }

        filtered = applyStageFilter(filtered, activeTab, filterValue);
        return sortFacilities(filtered, activeTab, sortValue || defaultSort);
    }, [grouped, search, filterValue, sortValue, activeTab, defaultSort]);

    const navigateToFacility = (f: Facility) =>
        navigate(`/admin/facilities/${f._id}`);

    const onboardingColumns = useMemo(
        () =>
            createOnboardingColumns({
                onInviteAdmin: (f) => setInviteTarget(f),
                onDeactivate: setDeactivateTarget,
            }),
        []
    );
    const trialColumns = useMemo(
        () => createTrialColumns({ onDeactivate: setDeactivateTarget }),
        []
    );
    const activeColumns = useMemo(
        () => createActiveColumns({ onDeactivate: setDeactivateTarget }),
        []
    );

    const columns =
        activeTab === 'trial'
            ? trialColumns
            : activeTab === 'active'
            ? activeColumns
            : onboardingColumns;

    return (
        <div className="mx-auto w-11/12 max-w-6xl py-8">
            {/* Header */}
            <div className="mb-8 flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">
                        Facilities
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Monitor onboarding, trial, and active facilities.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                        New Facility
                    </Button>
                </div>
            </div>

            {/* Chips + search + filters */}
            <div className="mb-7 flex flex-wrap items-center gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {STAGE_TABS.map((tab) => {
                        const isActive = activeTab === tab.value;
                        return (
                            <button
                                key={tab.value}
                                onClick={() => setActiveTab(tab.value)}
                                className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                                    isActive ? CHIP_ACTIVE : CHIP_INACTIVE
                                }`}
                            >
                                {tab.label}
                                <span
                                    className={`ml-2 text-xs ${
                                        isActive
                                            ? 'text-zinc-500'
                                            : 'opacity-50'
                                    }`}
                                >
                                    {grouped[tab.value].length}
                                </span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setActiveTab('guests')}
                        className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'guests' ? CHIP_ACTIVE : CHIP_INACTIVE
                        }`}
                    >
                        Guests
                    </button>
                </div>


                {activeTab !== 'all' && activeTab !== 'guests' && filterOptions.length > 1 && (
                    <Select
                        value={filterValue}
                        onValueChange={setFilterValue}
                    >
                        <SelectTrigger className="h-9 w-full border-white/[0.14] bg-white/[0.06] text-sm sm:w-auto">
                            <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                            {filterOptions.map((opt) => (
                                <SelectItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                {activeTab !== 'all' && activeTab !== 'guests' && sortOptions.length > 0 && (
                    <Select
                        value={sortValue || defaultSort}
                        onValueChange={setSortValue}
                    >
                        <SelectTrigger className="h-9 w-full border-white/[0.14] bg-white/[0.06] text-sm sm:w-auto">
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            {sortOptions.map((opt) => (
                                <SelectItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <div className="relative min-w-0 flex-1 sm:ml-auto">
                    <Search
                        className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        aria-hidden="true"
                    />
                    <Input
                        aria-label="Search facilities"
                        placeholder="Search..."
                        className="h-9 border-white/[0.14] bg-white/[0.06] pl-10 text-sm placeholder:text-muted-foreground/70 focus-visible:border-white/[0.28]"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Tables */}
            {activeTab === 'guests' ? (
                <GuestsSection search={search} />
            ) : loading ? (
                <div className="flex justify-center py-16 text-sm text-muted-foreground">
                    <LoadingRing size="large" />
                </div>
            ) : activeTab === 'all' ? (
                <div className="space-y-8">
                    <StackedSection
                        label="Onboarding"
                        count={grouped.onboarding.length}
                        facilities={grouped.onboarding}
                        columns={onboardingColumns}
                        search={search}
                        onRowClick={navigateToFacility}
                    />
                    <StackedSection
                        label="Trial"
                        count={grouped.trial.length}
                        facilities={grouped.trial}
                        columns={trialColumns}
                        search={search}
                        onRowClick={navigateToFacility}
                    />
                    <StackedSection
                        label="Active"
                        count={grouped.active.length}
                        facilities={grouped.active}
                        columns={activeColumns}
                        search={search}
                        onRowClick={navigateToFacility}
                    />
                </div>
            ) : (
                <FacilitiesTable
                    facilities={filteredRows}
                    columns={columns}
                    emptyMessage="No facilities match the current filters."
                    onRowClick={navigateToFacility}
                />
            )}

            <FacilityDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onSaved={async (facility) => {
                    await fetchFacilities();
                    if (facility?._id) {
                        navigate(`/admin/facilities/${facility._id}`);
                    }
                }}
                viewMode="admin"
            />

            {inviteTarget && (
                <InviteSiteAdminDialog
                    open={!!inviteTarget}
                    onOpenChange={(open) => {
                        if (!open) setInviteTarget(null);
                    }}
                    departmentId={inviteTarget._id}
                    facilityName={inviteTarget.name}
                    onInvited={fetchFacilities}
                />
            )}
            <DeactivateFacilityDialog
                facility={deactivateTarget}
                onOpenChange={(open) => {
                    if (!open) setDeactivateTarget(null);
                }}
                onDeactivated={fetchFacilities}
            />
        </div>
    );
}
